// components/community/channelSections.ts — SHARED channels-model helpers.
//
// Pure functions for turning a flat channel list (or the grouped-channels
// contract) into the pinned tiles + grouped sections rendered by the
// channels-model interior. Used by BOTH ServerDetailScreen (servers) and
// CommunityScreen (communities) so the two interiors stay identical and there is
// zero duplication.
//
// Grouped contract (servers & communities, ?grouped=true):
//   { success, data: { sections: [{ key, channels: [{id,name,type,unread,isNew}] }] } }
// Flat fallback: a plain channel array, from which sections are derived
// client-side (CATEGORY channels become section headers via parentId/categoryId;
// everything else lands in "All Channels" so nothing is ever dropped).
import type { ChannelTileData } from './ChannelTiles';

export interface Channel {
  id: string;
  name: string;
  type?: string;
  parentId?: string | null;
  categoryId?: string | null;
  position?: number;
  topic?: string | null;
  createdAt?: string;
}

export interface ChannelSection {
  name: string;
  channels: ChannelTileData[];
}

export const isCategory = (c: Channel) => (c.type || '').toUpperCase().includes('CATEGORY');
export const isVoice = (c?: { type?: string }) => (c?.type || '').toUpperCase().includes('VOICE');
export const byPos = (a: Channel, b: Channel) => (a.position ?? 0) - (b.position ?? 0);

// A channel created in the last 14 days reads as "New!" in the fallback path
// (the grouped endpoint sends an explicit isNew when it lands).
export function isRecent(createdAt?: string): boolean {
  if (!createdAt) return false;
  const ts = new Date(createdAt).getTime();
  return Number.isFinite(ts) && Date.now() - ts < 14 * 24 * 60 * 60 * 1000;
}

export function toTile(c: Channel): ChannelTileData {
  return { id: c.id, name: c.name, type: c.type, unread: 0, isNew: isRecent(c.createdAt) };
}

// CLIENT-SIDE section derivation (fallback): CATEGORY channels become section
// headers (children matched on parentId OR categoryId — the backend has written
// both over time); everything ungrouped lands in "All Channels". Orphans whose
// parent id matches no category are folded into "All Channels" so no channel is
// ever silently dropped.
export function deriveSections(channels: Channel[]): ChannelSection[] {
  const cats = channels.filter(isCategory).sort(byPos);
  const catIds = new Set(cats.map((c) => c.id));
  const content = channels.filter((c) => !isCategory(c));
  const parentOf = (c: Channel) => c.parentId ?? c.categoryId ?? null;

  const top = content.filter((c) => {
    const pid = parentOf(c);
    return pid === null || !catIds.has(pid); // ungrouped + orphans
  }).sort(byPos);

  const sections: ChannelSection[] = [];
  if (top.length > 0) sections.push({ name: 'All Channels', channels: top.map(toTile) });
  for (const cat of cats) {
    const kids = content.filter((c) => parentOf(c) === cat.id).sort(byPos);
    if (kids.length > 0) sections.push({ name: cat.name, channels: kids.map(toTile) });
  }
  return sections;
}

// Parse the grouped-channels contract defensively. Returns null when the body
// isn't the grouped shape so the caller can fall back to flat-derive.
export function parseGroupedSections(data: any): ChannelSection[] | null {
  const raw = Array.isArray(data?.sections) ? data.sections
    : (Array.isArray(data) && data.length > 0 && Array.isArray(data[0]?.channels)) ? data
    : null;
  if (!raw) return null;
  const sections: ChannelSection[] = [];
  for (const s of raw) {
    if (!s || !Array.isArray(s.channels)) continue;
    const channels: ChannelTileData[] = s.channels
      .filter((c: any) => c && c.id && c.name)
      .map((c: any) => ({
        id: String(c.id),
        name: String(c.name),
        type: c.type,
        unread: typeof c.unread === 'number' ? c.unread : 0,
        isNew: !!c.isNew,
        liveCount: typeof c.liveCount === 'number' ? c.liveCount : 0,
      }));
    if (channels.length > 0) sections.push({ name: s.key || s.name || 'Channels', channels });
  }
  return sections.length > 0 ? sections : null;
}

// Pinned/featured tiles: prefer the classic trio by name (General/Announcements/
// Random per the reference), then fill to 3 with the first text channels.
export function pickPinned(sections: ChannelSection[]): ChannelTileData[] {
  const all = sections.flatMap((s) => s.channels);
  const picked: ChannelTileData[] = [];
  for (const want of ['general', 'announcements', 'announcement', 'random']) {
    if (picked.length >= 3) break;
    const hit = all.find((c) => c.name.toLowerCase() === want && !picked.some((p) => p.id === c.id));
    if (hit) picked.push(hit);
  }
  for (const c of all) {
    if (picked.length >= 3) break;
    if (!picked.some((p) => p.id === c.id) && !isVoice(c)) picked.push(c);
  }
  return picked;
}
